import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Snackbar,
  Alert,
  Grid,
} from '@mui/material';
import TerminalModal from './terminalModal';
import PasswordModal from './passwordModal';
import InventoryReviewModal from './inventoryReviewModal';
import RecentFormsModal from './recentFormsModal';
import RecentConsumablesModal from './recentConsumablesModal';
import AuroraSearchForm from './auroraSearchForm';
import AuroraRetailerOverview from './auroraRetailerOverview';
import {
  ActiveTerminalsPanel,
  RecentOrdersPanel,
  RecentFormsPanel,
} from './auroraActivityPanels';
import { useAuroraRetailerSearch } from '../helpers/useAuroraRetailerSearch';
import {
  formatEmail,
  formatPhoneForDisplay,
  formatPhoneHref,
} from '../helpers/utils';

export const RetailerSearch = () => {
  const {
    isAuroraAdmin,
    translations,
    jurisdiction,
    setJurisdiction,
    retailerNumber,
    setRetailerNumber,
    searchLoading,
    retailer,
    reps,
    repsLoading,
    isSnackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    terminals,
    terminalsLoading,
    passwordTellers,
    passwordVerificationLoading,
    isPasswordModalOpen,
    isInventoryReviewModalOpen,
    isRecentFormsModalOpen,
    recentForms,
    recentFormsLoading,
    isRecentConsumablesModalOpen,
    recentConsumables,
    hostSearchParams,
    isTerminalModalOpen,
    selectedTerminal,
    user,
    retailerSkills,
    csSkills,
    skillsLoading,
    handleCloseSnackbar,
    handleSearch,
    handleViewPasswordClick,
    handleInventoryReviewModal,
    handleOpenTerminalDetails,
    handleCloseTerminalModal,
    handleOpenRecentFormsModal,
    handleCloseRecentFormsModal,
    handleOpenRecentConsumablesModal,
    handleCloseRecentConsumablesModal,
    clearSearchResults,
    loadTerminals,
    setSnackbarMessage,
    setSnackbarSeverity,
    setIsSnackbarOpen,
    setSelectedTerminal,
    setIsPasswordModalOpen,
    setIsInventoryReviewModalOpen,
  } = useAuroraRetailerSearch();

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'var(--dust-white)',
          borderRadius: 2,
          py: 2,
          px: { xs: 1, sm: 2 },
          minHeight: 'calc(100vh - 35px - 64px - 32px)',
        }}
      >
        <Container maxWidth='xl' disableGutters>
          <Card
            sx={{
              border: `1px solid rgba(5, 5, 51, 0.1)`,
              borderRadius: 3,
              overflow: 'hidden',
              backgroundColor: 'white',
              boxShadow:
                '0 4px 12px rgba(5, 5, 51, 0.1), 0 12px 28px rgba(5, 5, 51, 0.12)',
            }}
          >
            <CardHeader
              title={
                translations.auroraRetailerSearch || 'Aurora Retailer Search'
              }
              subheader={
                translations.auroraRetailerSearchSubtitle ||
                'Search a retailer to view details, terminals, and recent activity.'
              }
              sx={{
                backgroundColor: 'var(--reach-blue)',
                '& .MuiCardHeader-title': {
                  fontFamily: 'BrightstarSans-Bold',
                  color: '#FFFFFF',
                },
                '& .MuiCardHeader-subheader': {
                  color: 'rgba(255, 255, 255, 0.85)',
                },
              }}
            />
            <Divider />
            <AuroraSearchForm
              jurisdiction={jurisdiction}
              retailerNumber={retailerNumber}
              setRetailerNumber={setRetailerNumber}
              onSearch={handleSearch}
              onJurisdictionChange={nextJurisdiction => {
                setJurisdiction(nextJurisdiction);
                clearSearchResults();
              }}
              onClear={() => {
                clearSearchResults();
                setJurisdiction('');
              }}
              searchLoading={searchLoading}
              isAuroraAdmin={isAuroraAdmin}
              skillsLoading={skillsLoading}
              retailerSkills={retailerSkills}
              csSkills={csSkills}
              translations={translations}
            />
            {retailer && (
              <CardContent sx={{ backgroundColor: '#FFFFFF' }}>
                <AuroraRetailerOverview
                  retailer={retailer}
                  reps={reps}
                  repsLoading={repsLoading}
                  passwordTellers={passwordTellers}
                  passwordVerificationLoading={passwordVerificationLoading}
                  onViewPasswordClick={handleViewPasswordClick}
                  onInventoryReviewClick={handleInventoryReviewModal}
                  translations={translations}
                  hostSearchParams={hostSearchParams}
                  jurisdiction={jurisdiction}
                  formatEmail={formatEmail}
                  formatPhoneForDisplay={formatPhoneForDisplay}
                  formatPhoneHref={formatPhoneHref}
                  formatContactDisplayName={contact =>
                    contact.lastName && contact.firstName
                      ? `${contact.lastName}, ${contact.firstName}`
                      : contact.lastName || contact.firstName || ''
                  }
                />

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} alignItems='stretch'>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <ActiveTerminalsPanel
                      terminals={terminals}
                      terminalsLoading={terminalsLoading}
                      translations={translations}
                      onOpenTerminalDetails={handleOpenTerminalDetails}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <RecentOrdersPanel
                      recentConsumables={recentConsumables}
                      recentConsumablesLoading={recentFormsLoading}
                      translations={translations}
                      onViewAllConsumables={handleOpenRecentConsumablesModal}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <RecentFormsPanel
                      recentForms={recentForms}
                      recentFormsLoading={recentFormsLoading}
                      translations={translations}
                      onViewAllForms={handleOpenRecentFormsModal}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            )}
          </Card>
        </Container>
      </Box>

      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={7500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <PasswordModal
        open={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        tellers={passwordTellers}
        translations={translations}
      />

      <InventoryReviewModal
        open={isInventoryReviewModalOpen}
        onClose={() => setIsInventoryReviewModalOpen(false)}
        site={hostSearchParams?.site}
        user={user}
        translations={translations}
      />

      <RecentFormsModal
        open={isRecentFormsModalOpen}
        onClose={handleCloseRecentFormsModal}
        forms={recentForms}
        loading={recentFormsLoading}
        translations={translations}
      />

      <RecentConsumablesModal
        open={isRecentConsumablesModalOpen}
        onClose={handleCloseRecentConsumablesModal}
        consumables={recentConsumables}
        loading={recentFormsLoading}
        translations={translations}
      />

      <TerminalModal
        open={isTerminalModalOpen}
        onClose={handleCloseTerminalModal}
        terminal={selectedTerminal}
        retailerId={hostSearchParams?.retailerNumber}
        site={hostSearchParams?.site}
        onActionSuccess={async () => {
          if (!hostSearchParams) {
            return;
          }
          const updatedList = await loadTerminals(hostSearchParams);
          if (selectedTerminal && updatedList.length) {
            const match = updatedList.find(
              current =>
                current.terminalOid === selectedTerminal.terminalOid ||
                current.terminalId === selectedTerminal.terminalId,
            );
            if (match) {
              setSelectedTerminal(match);
            }
          }
        }}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarSeverity={setSnackbarSeverity}
        setIsSnackbarOpen={setIsSnackbarOpen}
        user={user}
      />
    </>
  );
};
